import React, { useState } from "react";
import { usePresets, presetLadder } from "../../../components/PresetPicker.jsx";
import { createPreset, updatePreset, setPresetActive, seedPresets } from "../../../lib/presetCallables.js";
import { C, font, inp } from "../../../lib/styles.js";

const SEEDED_IDS = ["lidar_photo", "body_rig", "head_rig", "nimbletrack_photo", "artec_photo", "lidar_only", "artec_only", "body_rig_group"];
const newExport = () => ({ kind: "model", label: "default", format: "obj", polycount: null, textureSize: null, required: true, dataClass: "deliverable" });
const newBody = () => ({ name: "", description: "", gear: [], recipe: { tool: "manual", kind: null, preset: null, timeoutMinutes: null, params: {}, software: [] }, exports: [newExport()], previews: [], group: false, active: true });
const list = (s) => s.split(",").map((v) => v.trim()).filter(Boolean);
const number = (s) => s === "" ? null : Number(s);
const row = { display: "flex", flexWrap: "wrap", gap: 10, alignItems: "end", marginBottom: 12 };

export default function PresetsAdmin({ user }) {
  const [revision, setRevision] = useState(0);
  if (!["admin", "superadmin"].includes(user?.role)) return <p style={{ padding: 24 }}>Only an admin manages presets.</p>;
  return <LoadedPresets key={revision} onReload={() => setRevision((r) => r + 1)} />;
}
function LoadedPresets({ onReload }) {
  const state = usePresets({ includeInactive: true });
  return <PresetsAdminView state={state} onReload={onReload} />;
}
export function PresetsAdminView({ state, onReload }) {
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const act = async (fn) => {
    setBusy(true); setError(null);
    try { await fn(); onReload(); }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };
  return <div style={{ padding: 24, fontFamily: font, color: C.text, fontSize: 13 }}>
    <h2 style={{ fontSize: 18 }}>Deliverable presets</h2>
    {(error || state.error) && <p role="alert" style={{ color: C.danger }}>{error || state.error}</p>}
    {state.loading ? <p>Loading…</p> : <>
      <div style={row}>
        {!state.error && SEEDED_IDS.some((id) => !state.presets.some((p) => p.presetId === id)) &&
          <button type="button" disabled={busy} onClick={() => act(seedPresets)}>Add the first presets</button>}
        <button type="button" disabled={busy} onClick={() => setEditing({})}>New preset</button>
      </div>
      {state.presets.map((p) => <div key={p.presetId} style={{ padding: "12px 0", borderTop: `1px solid ${C.border}` }}>
        <strong>{p.name}</strong> · {p.gear.join(", ")} · {p.recipe.tool} {p.recipe.software.join(", ")}
        <div style={{ color: C.muted, margin: "5px 0" }}>{presetLadder(p)}</div>
        <label><input type="checkbox" checked={p.active} disabled={busy}
          onChange={() => act(() => setPresetActive({ presetId: p.presetId, active: !p.active }))} /> Active {p.active ? "on" : "off"}</label>{" "}
        <button type="button" disabled={busy} onClick={() => setEditing(p)}>Edit</button>
      </div>)}
    </>}
    {editing && <PresetForm key={editing.presetId || "new"} preset={editing} onSaved={onReload} onCancel={() => setEditing(null)} />}
  </div>;
}

function Field({ label, value, onChange, type = "text", disabled = false, options }) {
  const props = { style: { ...inp, padding: 7, marginTop: 4 }, value: value ?? "", disabled, onChange: (e) => onChange(e.target.value) };
  return <label style={{ display: "block", minWidth: 110, flex: "1 1 130px" }}>{label}
    {options ? <select {...props}>{options.map((v) => <option key={v} value={v}>{v || "None"}</option>)}</select> : <input {...props} type={type} />}
  </label>;
}
export function PresetForm({ preset, onSaved, onCancel }) {
  const [body, setBody] = useState(() => {
    if (!preset.presetId) return newBody();
    const { name, description, gear, recipe, exports, previews, group, active } = preset;
    return { name, description, gear, recipe, exports, previews, group, active };
  });
  const [presetId, setPresetId] = useState(preset.presetId || "");
  const [gear, setGear] = useState((preset.gear || []).join(", "));
  const [software, setSoftware] = useState((preset.recipe?.software || []).join(", "));
  const [tools, setTools] = useState((preset.recipe?.needs?.tools || []).join(", "));
  const [params, setParams] = useState(JSON.stringify(preset.recipe?.params || {}, null, 2));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const patch = (key, value) => setBody((b) => ({ ...b, [key]: value }));
  const recipe = (key, value) => setBody((b) => ({ ...b, recipe: { ...b.recipe, [key]: value } }));
  const item = (field, i, key, value) => patch(field, body[field].map((e, n) => n === i ? { ...e, [key]: value } : e));
  const save = async (e) => {
    e.preventDefault(); setBusy(true); setError(null);
    try {
      const input = { ...body, presetId, gear: list(gear), recipe: { ...body.recipe, params: JSON.parse(params), software: list(software), needs: { ...body.recipe.needs, tools: list(tools) } } };
      await (preset.presetId ? updatePreset(input) : createPreset(input));
      onSaved();
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };
  return <form onSubmit={save} style={{ border: `1px solid ${C.border}`, padding: 16, marginTop: 18, borderRadius: 8 }}>
    <h3>{preset.presetId ? "Edit preset" : "New preset"}</h3>
    <fieldset disabled={busy} style={{ border: 0, padding: 0, minWidth: 0 }}>
      <div style={row}>
        <Field label="Preset ID" value={presetId} onChange={setPresetId} disabled={!!preset.presetId} />
        <Field label="Name" value={body.name} onChange={(v) => patch("name", v)} />
        <Field label="Gear codes (comma separated)" value={gear} onChange={setGear} />
      </div>
      <label>Description<textarea style={inp} value={body.description} onChange={(e) => patch("description", e.target.value)} /></label>
      <h4>Recipe</h4>
      <div style={row}>
        <Field label="Tool" value={body.recipe.tool} options={["manual", "acworker", "realityscan", "artec", "cyclone", "lcc"]} onChange={(v) => recipe("tool", v)} />
        <Field label="Task kind" value={body.recipe.kind} options={["", "mesh", "lidar", "artec", "preview", "splat", "render", "verify", "contact_sheet"]} onChange={(v) => recipe("kind", v || null)} />
        <Field label="Bridge recipe" value={body.recipe.preset} onChange={(v) => recipe("preset", v || null)} />
        <Field label="Timeout (minutes)" type="number" value={body.recipe.timeoutMinutes} onChange={(v) => recipe("timeoutMinutes", number(v))} />
      </div>
      <div style={row}>
        <Field label="Software (comma separated)" value={software} onChange={setSoftware} />
        <Field label="Extra tool keys (comma separated)" value={tools} onChange={setTools} />
        {["vramGb", "ramGb", "diskGb"].map((key) => <Field key={key} label={{ vramGb: "VRAM (GB)", ramGb: "RAM (GB)", diskGb: "Disk (GB)" }[key]}
          type="number" value={body.recipe.needs?.[key]} onChange={(v) => {
            const needs = { ...body.recipe.needs };
            if (v === "") delete needs[key]; else needs[key] = Number(v);
            recipe("needs", needs);
          }} />)}
      </div>
      <label>Recipe parameters (JSON)<textarea style={inp} value={params} onChange={(e) => setParams(e.target.value)} /></label>
      <h4>Exports</h4>
      {body.exports.map((e, i) => <div key={i} style={{ ...row, paddingBottom: 10, borderBottom: `1px solid ${C.border}` }}>
        <Field label="Kind" value={e.kind} options={["model", "pointcloud", "hdr", "photoset", "raw", "project"]} onChange={(v) => item("exports", i, "kind", v)} />
        <Field label="Label" value={e.label} onChange={(v) => item("exports", i, "label", v)} />
        <Field label="Format" value={e.format} onChange={(v) => item("exports", i, "format", v)} />
        <Field label="Polycount" type="number" value={e.polycount} onChange={(v) => item("exports", i, "polycount", number(v))} />
        <Field label="Texture" value={e.textureSize} options={["", "2k", "4k", "8k", "16k"]} onChange={(v) => item("exports", i, "textureSize", v || null)} />
        <Field label="Data class" value={e.dataClass} options={["raw", "deliverable", "project", "preview", "unclassified"]} onChange={(v) => item("exports", i, "dataClass", v)} />
        <label><input type="checkbox" checked={e.required} onChange={(v) => item("exports", i, "required", v.target.checked)} /> Required</label>
        <button type="button" onClick={() => patch("exports", body.exports.filter((_, n) => n !== i))}>Remove export</button>
      </div>)}
      <button type="button" onClick={() => patch("exports", [...body.exports, newExport()])}>Add export</button>
      <h4>Previews</h4>
      {body.previews.map((p, i) => <div key={i} style={row}>
        <Field label="Preview kind" value={p.kind} options={["preview_splat", "preview_ar", "preview_obj"]} onChange={(v) => item("previews", i, "kind", v)} />
        <Field label="Preview recipe" value={p.recipe} onChange={(v) => item("previews", i, "recipe", v)} />
        <button type="button" onClick={() => patch("previews", body.previews.filter((_, n) => n !== i))}>Remove preview</button>
      </div>)}
      <button type="button" onClick={() => patch("previews", [...body.previews, { kind: "preview_obj", recipe: "obj_lowres" }])}>Add preview</button>
      <div style={{ ...row, marginTop: 16 }}>
        <label><input type="checkbox" checked={body.group} onChange={(e) => patch("group", e.target.checked)} /> Group</label>
        <label><input type="checkbox" checked={body.active} onChange={(e) => patch("active", e.target.checked)} /> Active</label>
      </div>
      {error && <p role="alert" style={{ color: C.danger }}>{error}</p>}
      <button type="submit">{busy ? "Saving…" : "Save preset"}</button>{" "}
      <button type="button" onClick={onCancel}>Cancel</button>
    </fieldset>
  </form>;
}
